import React, { useState } from 'react';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  Container,
  Avatar,
  Button,
  Tooltip,
  MenuItem,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  useScrollTrigger,
  useMediaQuery,
  ButtonGroup,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import LanguageIcon from '@mui/icons-material/Language';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import { useSelector } from 'react-redux';
import { styled } from '@mui/material/styles';

// Màu sắc logo
const LOGO_RED_COLOR = '#D40511';
const YELLOW_COLOR = '#FFCC00';

// Xử lý cuộn để thay đổi màu AppBar
const ElevationScroll = (props) => {
  const { children } = props;
  const theme = useTheme();
  const location = useLocation();
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 0,
  });

  return React.cloneElement(children, {
    elevation: trigger ? 4 : 0,
    sx: {
      backgroundColor: YELLOW_COLOR,
      color: trigger ? theme.palette.text.primary : theme.palette.primary.contrastText,
      transition: 'all 0.3s ease-in-out',
      '& .MuiButton-root': {
        color: trigger ? theme.palette.text.primary : theme.palette.primary.contrastText,
      },
      '& .MuiIconButton-root': {
        color: trigger ? theme.palette.text.primary : theme.palette.primary.contrastText,
      },
      '& .MuiTypography-root': {
        color: LOGO_RED_COLOR,
      },
      '& .MuiTypography-root.logo-text': {
        color: LOGO_RED_COLOR,
      },
    },
  });
};

// StyledMenuItem với underline đỏ khi active
const StyledMenuItem = styled(Button)(({ theme }) => ({
  color: theme.palette.primary.contrastText,
  textTransform: 'none',
  fontSize: '1.2rem',
  padding: '8px 16px',
  position: 'relative',
  transition: 'all 0.3s ease',
  height: '100%',
  '&:hover': {
    backgroundColor: 'rgba(255, 204, 0, 0.3)',
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: 0,
      left: '50%',
      transform: 'translateX(-50%)',
      width: '40%',
      height: '3px',
      backgroundColor: LOGO_RED_COLOR,
      opacity: 0.7,
      borderRadius: '2px',
    },
  },
  '&.active': {
    backgroundColor: 'transparent',
    fontWeight: 600,
    color: '#000000',
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: 0,
      left: '50%',
      transform: 'translateX(-50%)',
      width: '60%',
      height: '4px',
      backgroundColor: LOGO_RED_COLOR,
      borderRadius: '2px',
    },
    '&:hover': {
      backgroundColor: 'rgba(255, 204, 0, 0.3)',
    },
  },
}));

const Header = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const user = useSelector((state) => state.auth.user);

  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorElUser, setAnchorElUser] = useState(null);
  const [anchorElLang, setAnchorElLang] = useState(null);
  
  // ---> THÊM STATE CHO MENU COMMUNITY Ở ĐÂY <---
  const [anchorElCommunity, setAnchorElCommunity] = useState(null);

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const handleOpenUserMenu = (event) => setAnchorElUser(event.currentTarget);
  const handleCloseUserMenu = () => setAnchorElUser(null);
  const handleOpenLangMenu = (event) => setAnchorElLang(event.currentTarget);
  const handleCloseLangMenu = () => setAnchorElLang(null);

  // ---> THÊM HÀM XỬ LÝ MỞ/ĐÓNG COMMUNITY DROPDOWN <---
  const handleOpenCommunity = (event) => setAnchorElCommunity(event.currentTarget);
  const handleCloseCommunity = () => setAnchorElCommunity(null);

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    localStorage.setItem('i18nextLng', lng);
    handleCloseLangMenu();
    window.location.reload();
  };

  // ---> CẬP NHẬT NAV ITEMS: Thêm isDropdown <---
  const navItems = [
    { name: t('header.home'), path: '/' },
    { name: t('header.services'), path: '/services' },
    { name: t('header.industries'), path: '/industries' },
    { name: t('header.blog'), path: '/blog' },
    { name: t('header.support'), path: '/support' },
    { name: 'Community', path: '/community', isDropdown: true }, // Thay Careers bằng Community
    { name: t('header.contact'), path: '/contact' },
    { name: t('header.about'), path: '/about' },
  ];

  const userMenuItems = isAuthenticated
    ? [
        { name: t('header.dashboard'), action: () => navigate('/dashboard') },
        { name: t('header.profile'), action: () => navigate('/profile') },
        { name: t('header.settings'), action: () => navigate('/settings') },
        { name: t('header.logout'), action: () => console.log('Logout') },
      ]
    : [
        { name: t('header.login'), action: () => navigate('/login') },
        { name: t('header.register'), action: () => navigate('/register') },
      ];

  const languages = [
    { code: 'en', name: 'EN' },
    { code: 'vi', name: 'VN' },
    { code: 'ja', name: 'JP' },
  ];

  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ textAlign: 'center', pt: 2 }}>
      <Box sx={{ my: 2, display: 'flex', justifyContent: 'center' }}>
        <img src="/assets/images/logo.png" alt="UniLM Logo" style={{ height: '50px', width: 'auto', mixBlendMode: 'multiply' }} />
      </Box>
      <Divider />
      <List>
        {navItems.map((item) => (
          <ListItem key={item.path} disablePadding>
            <ListItemButton 
              component={RouterLink} 
              // Nếu là dropdown, trên mobile tạm thời click vào sẽ trỏ thẳng đến /careers
              to={item.isDropdown ? '/careers' : item.path} 
              sx={{ textAlign: 'center', py: 1.5 }}
            >
              <ListItemText primary={item.name} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider sx={{ my: 2 }} />
      <Box sx={{ px: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
          <LanguageIcon sx={{ mr: 1, color: 'text.secondary' }} />
          <Typography variant="subtitle1" fontWeight="600">{t('header.changeLanguage') || 'Ngôn ngữ'}</Typography>
        </Box>
        <ButtonGroup fullWidth variant="contained">
          {languages.map((language) => {
            const isActive = i18n.resolvedLanguage === language.code;
            return (
              <Button
                key={language.code}
                onClick={() => changeLanguage(language.code)}
                sx={{
                  backgroundColor: isActive ? theme.palette.primary.main : theme.palette.grey[200],
                  color: isActive ? theme.palette.primary.contrastText : theme.palette.text.primary,
                  fontWeight: isActive ? 'bold' : 'normal',
                  borderColor: theme.palette.primary.main,
                  '&:hover': {
                    backgroundColor: isActive ? theme.palette.primary.dark : theme.palette.grey[300],
                    borderColor: theme.palette.primary.main,
                  },
                }}
              >
                {language.name}
              </Button>
            );
          })}
        </ButtonGroup>
      </Box>
    </Box>
  );

  return (
    <div className="pb-20">
      <ElevationScroll>
        <AppBar position="fixed" sx={{ boxShadow: 'none', borderBottom: '1px solid', borderColor: 'divider' }}>
          <Container maxWidth="xl">
            <Toolbar disableGutters>
              {/* Mobile: Hamburger + Logo */}
              <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', flexGrow: 1 }}>
                <IconButton size="large" edge="start" onClick={handleDrawerToggle} color="inherit" sx={{ mr: 2 }}>
                  <MenuIcon />
                </IconButton>
                <Box component={RouterLink} to="/" sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center', textDecoration: 'none' }}>
                  <img src="/assets/images/logo.png" alt="UniLM Logo" style={{ height: '40px', width: 'auto', mixBlendMode: 'multiply' }} />
                </Box>
              </Box>

              {/* Desktop: Logo */}
              <Box component={RouterLink} to="/" sx={{ mr: 2, display: { xs: 'none', md: 'flex' }, alignItems: 'center', textDecoration: 'none' }}>
                <img src="/assets/images/logo.png" alt="UniLM Logo" style={{ height: '50px', width: 'auto', mixBlendMode: 'multiply' }} />
              </Box>

              {/* Desktop Menu */}
              <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' } }}>
                {navItems.map((item) => {
                  const currentPath = location.pathname;
                  
                  // Logic kiểm tra Active
                  const isActive = (() => {
                    if (item.path === '/' && currentPath === '/') return true;
                    if (item.path === '/' && currentPath !== '/') return false;
                    if (item.path === '/blog') return currentPath.startsWith('/blog');
                    
                    // Nếu là mục Community, cho nó Active (gạch đỏ dưới chân) khi user ở các trang con
                    if (item.isDropdown && (
                        currentPath.includes('/careers') || 
                        currentPath.includes('/internship') || 
                        currentPath.includes('/training')
                    )) {
                        return true;
                    }

                    const cleanCurrent = currentPath.endsWith('/') ? currentPath.slice(0, -1) : currentPath;
                    const cleanItem = item.path.endsWith('/') ? item.path.slice(0, -1) : item.path;
                    return cleanCurrent === cleanItem;
                  })();

                  // ---> NẾU LÀ DROPDOWN COMMUNITY <---
                  if (item.isDropdown) {
                    return (
                      <Box 
                        key={item.path} 
                        onMouseLeave={handleCloseCommunity} 
                        sx={{ display: 'inline-block', height: '100%' }}
                      >
                        {/* Dùng CHÍNH XÁC StyledMenuItem của bạn làm nút bấm */}
                        <StyledMenuItem
                          className={isActive ? 'active' : ''}
                          onMouseEnter={handleOpenCommunity}
                          onClick={handleOpenCommunity}
                          aria-controls={Boolean(anchorElCommunity) ? 'community-menu' : undefined}
                          aria-haspopup="true"
                        >
                          {item.name}
                        </StyledMenuItem>
                        
                        {/* Dropdown Menu */}
                        <Menu
                          id="community-menu"
                          anchorEl={anchorElCommunity}
                          open={Boolean(anchorElCommunity)}
                          onClose={handleCloseCommunity}
                          MenuListProps={{
                            onMouseLeave: handleCloseCommunity,
                            sx: { py: 0 }
                          }}
                          PaperProps={{
                            elevation: 4,
                            sx: { mt: 0.5, minWidth: 260, borderRadius: '2px', border: '1px solid #e0e0e0' }
                          }}
                          style={{ pointerEvents: 'none' }}
                          disableScrollLock={true}
                        >
                          <Box sx={{ pointerEvents: 'auto' }}>
                            <MenuItem 
                              onClick={() => { navigate('/careers'); handleCloseCommunity(); }}
                              sx={{ py: 2, px: 3, textTransform: 'uppercase', fontSize: '0.9rem', fontWeight: 500 }}
                            >
                              Careers
                            </MenuItem>
                            <Divider sx={{ my: 0 }} />
                            <MenuItem 
                              onClick={() => { navigate('/internship'); handleCloseCommunity(); }}
                              sx={{ py: 2, px: 3, textTransform: 'uppercase', fontSize: '0.9rem', fontWeight: 500 }}
                            >
                              Free Internship Program
                            </MenuItem>
                            <Divider sx={{ my: 0 }} />
                            <MenuItem 
                              onClick={() => { navigate('/training'); handleCloseCommunity(); }}
                              sx={{ py: 2, px: 3, textTransform: 'uppercase', fontSize: '0.9rem', fontWeight: 500 }}
                            >
                              Training Programs
                            </MenuItem>
                          </Box>
                        </Menu>
                      </Box>
                    );
                  }

                  // Render các nút menu thường
                  return (
                    <StyledMenuItem
                      key={item.path}
                      component={RouterLink}
                      to={item.path}
                      className={isActive ? 'active' : ''}
                    >
                      {item.name}
                    </StyledMenuItem>
                  );
                })}
              </Box>

              {/* Language Menu - Desktop */}
              <Box sx={{ flexGrow: 0, display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}>
                <Tooltip title={t('header.changeLanguage')}>
                  <IconButton onClick={handleOpenLangMenu} sx={{ p: 0, mx: 1 }}>
                    <LanguageIcon />
                  </IconButton>
                </Tooltip>
                <Menu
                  sx={{ mt: '45px' }}
                  id="menu-language"
                  anchorEl={anchorElLang}
                  anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                  keepMounted
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  open={Boolean(anchorElLang)}
                  onClose={handleCloseLangMenu}
                >
                  {languages.map((language) => (
                    <MenuItem key={language.code} onClick={() => changeLanguage(language.code)}>
                      <Typography textAlign="center">{language.name}</Typography>
                    </MenuItem>
                  ))}
                </Menu>
              </Box>
            </Toolbar>
          </Container>

          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{ keepMounted: true }}
            sx={{
              display: { xs: 'block', md: 'none' },
              '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 280 },
            }}
          >
            {drawer}
          </Drawer>
        </AppBar>
      </ElevationScroll>
    </div>
  );
};

export default Header;